/**
 * See https://hello.atlassian.net/wiki/spaces/JSDee/pages/2143585806/Faux+Spa+-+Closing+the+tab
 */
import { useCallback, useRef, useEffect } from 'react';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import type {
	IsSameOriginNavigationAPIProps,
	NavigateEvent,
	NavigationBlockProps,
} from './types.tsx';

function isSameOrigin(destination: string) {
	const destinationOrigin = new URL(destination).origin;

	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	return window.location.origin === destinationOrigin;
}

const useIsSameOriginNavigationAPI = ({
	isNavigatingWithinSameOrigin,
	isEnabled,
}: IsSameOriginNavigationAPIProps) => {
	const onNavigate = useCallback(
		(e: NavigateEvent) => {
			if (e.destination.sameDocument) {
				// when we navigate through the same document (spa -> spa) onBeforeUnload won't be called, so we can instead reset isNavigatingWithinSameOrigin
				// eslint-disable-next-line no-param-reassign, react-hooks/react-compiler
				isNavigatingWithinSameOrigin.current = false; // reset
			} else {
				// eslint-disable-next-line no-param-reassign
				isNavigatingWithinSameOrigin.current = isSameOrigin(e.destination.url);
			}
			return isNavigatingWithinSameOrigin.current;
		},
		[isNavigatingWithinSameOrigin],
	);

	useEffect(() => {
		// only available in newer versions of chrome, opera, and edge
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
		const { navigation } = window as any;
		if (isEnabled && navigation) {
			navigation.addEventListener('navigate', onNavigate);
			return () => navigation.removeEventListener('navigate', onNavigate);
		}
		return undefined;
	}, [isEnabled, onNavigate]);

	// for testing
	return {
		onNavigate,
	};
};

export const useNavigationBlock = ({
	forceBlock = false,
	shouldAttemptNavigationBlock,
}: NavigationBlockProps) => {
	const isNavigatingWithinSameOrigin = useRef<boolean>(false);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
	const hasNavigationApi = Boolean((window as any).navigation);

	const { onNavigate } = useIsSameOriginNavigationAPI({
		isEnabled: hasNavigationApi,
		isNavigatingWithinSameOrigin,
	});

	const onBeforeUnload = useCallback(
		(e: Event) => {
			const analyticsPayload = {
				hasNavigationApi,
				forceBlock,
				isNavigatingWithinSameOrigin: isNavigatingWithinSameOrigin.current,
				shouldAttemptNavigationBlock,
			};

			const shouldBlockNavigation =
				forceBlock || (shouldAttemptNavigationBlock && !isNavigatingWithinSameOrigin.current);

			isNavigatingWithinSameOrigin.current = false; // reset

			if (shouldBlockNavigation) {
				fireOperationalAnalytics(
					createAnalyticsEvent({}),
					'useNavigationBlock showBrowserUnloadPrompt',
					analyticsPayload,
				);

				e.preventDefault(); // Firefox
				const message = '';
				// @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'boolean'.
				e.returnValue = message; // Chrome
				return message; // IE
			}

			fireOperationalAnalytics(
				createAnalyticsEvent({}),
				'useNavigationBlock dontShowBrowserUnloadPrompt',
				analyticsPayload,
			);
			return undefined;
		},
		[createAnalyticsEvent, forceBlock, hasNavigationApi, shouldAttemptNavigationBlock],
	);

	/**
	 * Note: beforeunload will not block user closing the tab if
	 * the user has not interacted with the page
	 */
	useEffect(() => {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.addEventListener('beforeunload', onBeforeUnload);

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		return () => window.removeEventListener('beforeunload', onBeforeUnload);
	}, [onBeforeUnload]);

	/**
	 * Returning these functions to expose them for unit testing
	 */
	return {
		onBeforeUnload,
		onNavigate,
	};
};
